import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useContactSubmit } from "../hooks/useContactSubmit";

const eyebrow = "text-[14px] tracking-[0.25em] uppercase text-primary-600 mb-3";
const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors";

const infoIcons = [
  "ri-map-pin-line",
  "ri-mail-line",
  "ri-phone-line",
  "ri-time-line",
];

type InfoItem = { label: string; value: string };

export function ContactHero() {
  const { t } = useTranslation();
  return (
    <section className="relative h-[360px] md:h-[460px] w-full overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Cinematic%20aerial%20view%20of%20a%20modern%20container%20port%20at%20dusk%20with%20stacked%20shipping%20containers%20and%20a%20large%20cargo%20ship%20docked%20beside%20deep%20blue%20ocean%20water%2C%20cranes%20and%20dock%20structures%2C%20rich%20navy%20blue%20tones%2C%20moody%20atmospheric%20lighting%2C%20wide%20spacious%20composition%20with%20dark%20tones%20on%20the%20left%20for%20clean%20text%20overlay&width=1920&height=900&seq=lam-contact-port-01&orientation=landscape"
        alt="Container port with cargo ship on deep blue ocean"
        title="Contact EverRice rice exporter"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/20 to-foreground-950/45" />
      <div className="relative h-full flex items-center page-gutter">
        <div className="max-w-xl fade-up pt-16 text-shadow-soft">
          <p className="font-heading text-lg tracking-[0.3em] text-background-50/85 mb-4">
            {t("contactPage.hero.eyebrow")}
          </p>
          <h1 className="font-heading text-4xl md:text-[3.625rem] leading-[1.1] tracking-[0.03em] text-background-50 mb-6">
            {t("contactPage.hero.title")}
          </h1>
          <p className="text-sm md:text-base text-background-50/90 leading-relaxed">
            {t("contactPage.hero.desc")}
          </p>
        </div>
      </div>
    </section>
  );
}

export function ContactMain() {
  const { t } = useTranslation();
  const contactInfo = t("contactPage.main.info", {
    returnObjects: true,
  }) as unknown as InfoItem[];

  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [formError, setFormError] = useState("");

  const contactMutation = useContactSubmit();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // 🛡️ Kiểm tra Honeypot (Chống Spam)
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const phone = String(formData.get("phone") ?? "").trim() || undefined;
    const message = String(formData.get("message") ?? "").trim();

    setStatus("submitting");
    setFormError("");

    // 🚀 Gọi Mutation API qua Hook
    contactMutation.mutate(
      { name, email, phone, message },
      {
        onSuccess: () => {
          setStatus("success");
          form.reset();
        },
        onError: (error: any) => {
          setStatus("error");
          const serverMsg = error?.response?.data?.message;
          if (Array.isArray(serverMsg)) {
            setFormError(serverMsg.join(", "));
          } else {
            setFormError(serverMsg || t("contactPage.form.errorDefault"));
          }
        },
      },
    );
  };

  return (
    <section className="bg-background-50 page-gutter py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-12 items-start">
        <div>
          <p className={eyebrow}>{t("contactPage.main.eyebrow")}</p>
          <h2 className="font-heading text-3xl md:text-[1.8125rem] leading-tight text-foreground-950 mb-5">
            {t("contactPage.main.title")}
          </h2>
          <p className="text-sm text-foreground-700 leading-relaxed mb-10 max-w-md">
            {t("contactPage.main.desc")}
          </p>

          <div className="space-y-6">
            {Array.isArray(contactInfo) &&
              contactInfo.map((c, i) => (
                <div key={c.label || i} className="flex gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-full bg-secondary-100 flex items-center justify-center">
                    <i
                      className={`${infoIcons[i]} text-xl text-secondary-900`}
                    />
                  </div>
                  <div>
                    <p className="text-[11px] tracking-widest uppercase text-foreground-600 mb-1">
                      {c.label}
                    </p>
                    <p className="text-sm text-foreground-900 leading-relaxed">
                      {c.value}
                    </p>
                  </div>
                </div>
              ))}
          </div>

          <div className="flex flex-wrap gap-4 mt-10">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer"
            >
              {t("contactPage.main.ctaProducts")}{" "}
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/global-reach"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-foreground-900 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer hover:bg-foreground-950 hover:text-background-50 transition-colors"
            >
              {t("contactPage.main.ctaReach")} <i className="ri-earth-line" />
            </Link>
          </div>
        </div>

        <div
          id="send-message"
          className="scroll-mt-28 bg-background-100 rounded-lg border border-background-200 p-6 md:p-10"
        >
          <h3 className="font-heading text-2xl text-foreground-950 mb-2">
            {t("contactPage.form.title")}
          </h3>
          <p className="text-xs text-foreground-600 mb-8">
            {t("contactPage.form.note")}
          </p>

          <form id="contact-form" onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label
                  htmlFor="contact-name"
                  className="block text-[11px] tracking-widest uppercase text-foreground-700 mb-2"
                >
                  {t("contactPage.form.name")} *
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder={t("contactPage.form.namePh")}
                  className={inputClass}
                />
              </div>
              <div>
                <label
                  htmlFor="contact-email"
                  className="block text-[11px] tracking-widest uppercase text-foreground-700 mb-2"
                >
                  {t("contactPage.form.email")} *
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder={t("contactPage.form.emailPh")}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="contact-phone"
                className="block text-[11px] tracking-widest uppercase text-foreground-700 mb-2"
              >
                {t("contactPage.form.phone")}
              </label>
              <input
                id="contact-phone"
                name="phone"
                type="tel"
                placeholder={t("contactPage.form.phonePh")}
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="contact-message"
                className="block text-[11px] tracking-widest uppercase text-foreground-700 mb-2"
              >
                {t("contactPage.form.message")} *
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder={t("contactPage.form.messagePh")}
                className={`${inputClass} resize-none`}
              />
            </div>

            {/* Field Honeypot ẩn */}
            <div className="hidden" aria-hidden="true">
              <label htmlFor="contact-extra">Website</label>
              <input
                id="contact-extra"
                name="website_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <button
              type="submit"
              disabled={status === "submitting"}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer transition-colors"
            >
              {status === "submitting"
                ? t("contactPage.form.sending")
                : t("contactPage.form.submit")}{" "}
              <i className="ri-send-plane-line" />
            </button>

            {status === "success" && (
              <p className="flex items-center gap-2 text-sm text-primary-700 rounded-md bg-primary-100 px-4 py-3 animate-in fade-in">
                <i className="ri-checkbox-circle-line text-lg" />{" "}
                {t("contactPage.form.success")}
              </p>
            )}
            {status === "error" && (
              <p className="flex items-center gap-2 text-sm text-foreground-900 rounded-md bg-accent-100 px-4 py-3 animate-in fade-in">
                <i className="ri-error-warning-line text-lg" /> {formError}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

export function ContactMap() {
  const { t } = useTranslation();
  return (
    <section className="bg-background-100 page-gutter py-16 md:py-24">
      <div className="text-center mb-10">
        <p className={eyebrow}>{t("contactPage.map.eyebrow")}</p>
        <h2 className="font-heading text-3xl md:text-5xl leading-tight text-foreground-950">
          {t("contactPage.map.title")}
        </h2>
      </div>
      <div className="w-full h-[360px] md:h-[460px] rounded-lg overflow-hidden border border-background-200">
        <iframe
          title="EverRice office location in Ho Chi Minh City"
          src="https://www.google.com/maps?q=The%20Everich%20Tower%20968%203%2F2%20Street%20Phu%20Tho%20Ward%20Ho%20Chi%20Minh%20City%20Vietnam&output=embed"
          className="w-full h-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </section>
  );
}
